import React from 'react';
import moment from 'moment';
import 'moment-timezone';
import { capitalize } from 'lodash';
import { Tag, TagVariant } from '../components/tag';
import { Promotion, PromotionAvailability } from '../stores/promotion';
import { PromotionStatuses } from './const';
import { DEFAULT_TIMEZONE } from './timezone';

type PromotionStatus = typeof PromotionStatuses[number];

interface AvailabilityCheck {
    currentAvailability: PromotionAvailability | null;
    isDailyLimitReached: boolean;
}

interface NextAvailabilityResult {
    nextAvailability: PromotionAvailability | null;
    nextDate: Date | null;
}

interface DayInfo {
    date: Date;
    dayOfWeek: number;
}

const DAYS_IN_WEEK = 7;

/**
 * Gets the next 7 days starting from today, in the promotion's timezone.
 * This ensures availability windows are evaluated in the correct timezone
 * (e.g. "Monday 2-6 PM Eastern" is checked against Eastern time, not browser local).
 */
const getNextWeekDates = (
    now: Date,
    timezone: string = DEFAULT_TIMEZONE
): DayInfo[] => {
    return Array.from({ length: DAYS_IN_WEEK }, (_, i) => {
        const m = moment(now).tz(timezone).add(i, 'days').startOf('day');
        return {
            date: m.toDate(),
            dayOfWeek: m.day(),
        };
    });
};

/**
 * Checks if two moments represent the same calendar day in the given timezone.
 * Needed because Date.getDate() uses browser's local timezone, not the promotion's.
 */
const isSameDayInTimezone = (
    date1: Date,
    date2: Date,
    timezone: string
): boolean =>
    moment(date1).tz(timezone).format('YYYY-MM-DD') ===
    moment(date2).tz(timezone).format('YYYY-MM-DD');

const validatePromotionStatus = (
    status: string | null | undefined
): PromotionStatus | null => {
    if (!status) return null;

    const validStatuses = PromotionStatuses as readonly string[];
    if (validStatuses.includes(status)) {
        return status as PromotionStatus;
    }

    return null;
};

/**
 * Checks if an availability window is outside its valid date range.
 *
 * An availability can have optional start/end dates that further
 * restrict when it's valid, beyond just the day of week and hours.
 */
const availabilityOutOfTimeRange = (
    availability: PromotionAvailability,
    date: Date
): boolean => {
    // Check if availability hasn't started yet
    if (availability.startDate) {
        const startDate = new Date(
            moment(availability.startDate).toISOString()
        );
        if (startDate > date) return true;
    }

    // Check if availability has ended
    if (availability.endDate) {
        const endDate = new Date(moment(availability.endDate).toISOString());
        if (endDate < date) return true;
    }

    return false;
};

/**
 * Finds the next upcoming availability window within the next 7 days.
 * Uses the promotion's timezone so "Monday 2-6 PM Eastern" is evaluated in Eastern time.
 */
const findNextAvailability = (
    promotion: Promotion,
    now: Date,
    timezone: string = DEFAULT_TIMEZONE
): NextAvailabilityResult => {
    if (!promotion.availabilities) {
        return { nextAvailability: null, nextDate: null };
    }

    const nowInTz = moment(now).tz(timezone);
    const currentHour = nowInTz.hour();
    const nextWeekDates = getNextWeekDates(now, timezone);

    for (const dayInfo of nextWeekDates) {
        for (const availability of promotion.availabilities) {
            if (availabilityOutOfTimeRange(availability, dayInfo.date))
                continue;
            if (availability.dayOfWeek !== dayInfo.dayOfWeek) continue;

            // Skip if it's today but the time window has already passed
            const sameDayButPassed =
                isSameDayInTimezone(dayInfo.date, now, timezone) &&
                currentHour >= availability.endHour;
            if (sameDayButPassed) continue;

            return { nextAvailability: availability, nextDate: dayInfo.date };
        }
    }

    return { nextAvailability: null, nextDate: null };
};

/**
 * Searches for an availability window that matches the current time.
 * Uses the promotion's timezone so hours and day-of-week are evaluated correctly
 * (e.g. "Monday 2-6 PM Eastern" matches when it's 2-6 PM in Eastern, not browser local).
 *
 * Returns both the availability and whether daily activation limit
 * has been reached (for limiting promotion usage per day).
 */
const findCurrentAvailability = (
    promotion: Promotion,
    now: Date,
    timezone: string = DEFAULT_TIMEZONE
): AvailabilityCheck => {
    if (!promotion.availabilities) {
        return { currentAvailability: null, isDailyLimitReached: false };
    }

    const nowInTz = moment(now).tz(timezone);
    const currentDayOfWeek = nowInTz.day(); // 0 = Sunday, 6 = Saturday
    const currentHour = nowInTz.hour(); // 0-23

    for (const availability of promotion.availabilities) {
        if (availabilityOutOfTimeRange(availability, now)) continue;
        if (availability.dayOfWeek !== currentDayOfWeek) continue;

        // Hours from API are in promotion timezone (Eastern); compare directly
        const withinHours =
            currentHour >= availability.startHour &&
            currentHour < availability.endHour;
        if (withinHours) {
            const isDailyLimitReached =
                availability.dailyLimit > 0 &&
                (promotion.activationsCount || 0) >= availability.dailyLimit;

            return { currentAvailability: availability, isDailyLimitReached };
        }
    }

    return { currentAvailability: null, isDailyLimitReached: false };
};

const promotionEnded = (promotion: Promotion, now: Date): boolean =>
    now >= promotion.endDate ||
    (promotion.deactivationDisplayDate &&
        now >= promotion.deactivationDisplayDate);

const promotionNotStarted = (promotion: Promotion, now: Date): boolean =>
    now < promotion.startDate ||
    (promotion.activationReadyDate && now < promotion.activationReadyDate);

const getSimplePromotionStatus = (
    promotion: Promotion,
    now: Date
): PromotionStatus => {
    if (promotionNotStarted(promotion, now)) return 'upcoming';
    if (promotionEnded(promotion, now)) return 'ended';
    return 'activatable';
};

const getCurrentAvailabilityStatus = (
    promotion: Promotion,
    now: Date,
    timezone: string = DEFAULT_TIMEZONE
): PromotionStatus | null => {
    const {
        currentAvailability,
        isDailyLimitReached,
    } = findCurrentAvailability(promotion, now, timezone);

    if (!currentAvailability) {
        return null;
    }

    if (isDailyLimitReached) {
        return 'expired';
    }

    return 'activatable';
};

const allAvailabilitiesExpired = (
    availabilities: PromotionAvailability[],
    now: Date
): boolean => {
    return availabilities.every((availability) => {
        if (!availability.endDate) return false;
        return new Date(availability.endDate) < now;
    });
};

const getScheduledPromotionStatus = (
    promotion: Promotion,
    now: Date,
    timezone: string = DEFAULT_TIMEZONE
): PromotionStatus => {
    const availabilities = promotion.availabilities ?? [];

    // Check if all time windows have passed
    if (
        availabilities.length > 0 &&
        allAvailabilitiesExpired(availabilities, now)
    ) {
        return 'ended';
    }

    if (promotionEnded(promotion, now)) {
        return 'ended';
    }

    if (promotionNotStarted(promotion, now)) {
        return 'upcoming';
    }

    const currentStatus = getCurrentAvailabilityStatus(
        promotion,
        now,
        timezone
    );
    if (currentStatus !== null) {
        return currentStatus;
    }

    // Check if there's an upcoming availability window within the next week
    const { nextAvailability } = findNextAvailability(promotion, now, timezone);
    if (nextAvailability) {
        return 'upcoming';
    }

    // No availability windows found - use configured fallback status
    const outsideHoursStatus = validatePromotionStatus(
        promotion.items?.[0]?.outsideActivatableHours
    );
    return outsideHoursStatus ?? 'upcoming';
};

const hasScheduledAvailabilities = (promotion: Promotion): boolean =>
    Boolean(promotion.availabilities?.length);

/**
 * Determines the current status of a promotion based on its configuration,
 * schedule, and availability windows.
 *
 * Availabilities are evaluated in the promotion's timezone (e.g. "Monday 2-6 PM Eastern"
 * is checked against Eastern time), so status is consistent regardless of where the
 * CMS user is located.
 *
 * Priority order:
 * 1. Explicit 'upcoming' status
 * 2. Scheduled promotions (with availabilities)
 * 3. Simple promotions (date range only)
 *
 * @param promotion - The promotion to check
 * @param now - Optional current time (defaults to moment().toDate()). Useful for testing.
 */
export const getPromotionStatus = (
    promotion: Promotion,
    now: Date = moment().toDate()
): PromotionStatus => {
    if (promotion.status === 'upcoming') {
        return 'upcoming';
    }

    const timezone = promotion.timezone ?? DEFAULT_TIMEZONE;

    if (hasScheduledAvailabilities(promotion)) {
        return getScheduledPromotionStatus(promotion, now, timezone);
    }

    return getSimplePromotionStatus(promotion, now);
};

/*  
  Tags
*/

function getPromotionStatusConfig(
    status: PromotionStatus
): {
    variant: TagVariant;
    icon: string;
    label: string;
} {
    switch (status) {
        case 'activatable':
            return { variant: 'success', icon: '✓', label: 'Activable' };
        case 'upcoming':
            return { variant: 'blue', icon: '⏰', label: 'Upcoming' };
        case 'expired':
            return { variant: 'grey', icon: '', label: 'Expired' };
        case 'ended':
            return { variant: 'orange', icon: '⏹', label: 'Ended' };
        default:
            return { variant: 'grey', icon: '', label: capitalize(status) };
    }
}

export function PromotionStatusTag({
    status,
}: {
    status: PromotionStatus;
}): React.ReactElement {
    const { variant, icon, label } = getPromotionStatusConfig(status);

    return (
        <Tag variant={variant}>
            {icon && <span>{icon}</span>}
            {label}
        </Tag>
    );
}
